import React, { useState } from 'react';
import moment from 'moment';
// No need to import fetchAcademicYearTerms
const AcademicYearCard = ({ 
  academicYear, 
  onEdit, 
  onDelete, 
  onOpenCalendar,
  onMarkCompleted,
  onMarkSemesterCompleted,
  onMarkIndividualSemesterCompleted,
  onRollbackStatus,
  onRollbackSemester,
  onRestoreAcademicYear,
  onViewStatusHistory,
  loading,
  isReadOnly = false
}) => {
  const [showSemesterActions, setShowSemesterActions] = useState(false);
  const [showIndividualSemesterActions, setShowIndividualSemesterActions] = useState(false);
  const [, setShowStatusMenu] = useState(false);
  const [, setShowRecoveryMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Use academicYear.terms directly

  // No need to fetch terms, use academicYear.terms

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'inactive': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'completed': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'archived': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🔷';
      case 'inactive': return '⚪';
      case 'completed': return '✅';
      case 'archived': return '📦';
      default: return '⚪';
    }
  };

  const getAcademicYearId = () => academicYear.id || academicYear._id;

  const handleMarkCompleted = () => {
    onMarkCompleted(getAcademicYearId());
    setShowStatusMenu(false);
  };

  const handleMarkSemesterCompleted = (semesterType) => {
    onMarkSemesterCompleted(getAcademicYearId(), semesterType);
    setShowSemesterActions(false);
  };

  const handleMarkIndividualSemesterCompleted = (semesterId) => {
    onMarkIndividualSemesterCompleted(getAcademicYearId(), semesterId);
    setShowIndividualSemesterActions(false);
  };

  const handleRollbackToActive = () => {
    onRollbackStatus(getAcademicYearId(), 'active', 'Rollback to active status');
    setShowRecoveryMenu(false);
  };

  const handleRollbackToInactive = () => {
    onRollbackStatus(getAcademicYearId(), 'inactive', 'Rollback to inactive status');
    setShowRecoveryMenu(false);
  };

  const handleRollbackSemester = (semesterId) => {
    onRollbackSemester(getAcademicYearId(), semesterId, 'active', 'Rollback semester');
    setShowIndividualSemesterActions(false);
  };

  const handleRestore = () => {
    onRestoreAcademicYear(getAcademicYearId());
    setShowRecoveryMenu(false);
  };

  const handleViewStatusHistory = () => {
    onViewStatusHistory(getAcademicYearId());
    setShowStatusMenu(false);
  };

  const getCompletedSemesters = () => {
    if (!academicYear.semesters) return { odd: 0, even: 0, total: 0 };
    
    const oddCompleted = academicYear.semesters.filter(s => 
      s.semesterType === 'odd' && s.status === 'completed'
    ).length;
    
    const evenCompleted = academicYear.semesters.filter(s => 
      s.semesterType === 'even' && s.status === 'completed'
    ).length;

    return {
      odd: oddCompleted,
      even: evenCompleted,
      total: oddCompleted + evenCompleted
    };
  };

  const getSemestersByType = () => {
    if (!academicYear.semesters) return { odd: [], even: [] };
    
    return {
      odd: academicYear.semesters.filter(s => s.semesterType === 'odd'),
      even: academicYear.semesters.filter(s => s.semesterType === 'even')
    };
  };

  const canRestore = academicYear.isArchived && 
    academicYear.canRestoreUntil && 
    new Date(academicYear.canRestoreUntil) > new Date();

  const completedSemesters = getCompletedSemesters();
  const semestersByType = getSemestersByType();
  const isCompletedOrInactive = ['completed', 'inactive'].includes(academicYear.status);

  // Helper: Get display name with label
  const getDisplayName = () => {
    if (academicYear.label) {
      return `${academicYear.name} (${academicYear.label})`;
    }
    return academicYear.name;
  };

  // Main action buttons that are always visible
  const renderMainActionButtons = () => {
    if (isReadOnly) {
      return (
        <button
          onClick={handleViewStatusHistory}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 border border-purple-200 transition-all duration-200 font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          View History
        </button>
      );
    }

    if (academicYear.isArchived) {
      return (
        <div className="flex gap-3">
          <button
            onClick={handleRestore}
            disabled={!canRestore || loading}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 ${
              !canRestore || loading
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {canRestore ? 'Restore' : 'Restore Expired'}
          </button>
          <button
            onClick={handleViewStatusHistory}
            className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 border border-purple-200 transition-all duration-200 font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            View History
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-3">
        {/* Status Management - Always Visible */}
        {academicYear.status === 'active' && (
          <>
            <button
              onClick={handleMarkCompleted}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 border border-emerald-200 transition-all duration-200 font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Mark Completed
            </button>

            {academicYear.isSemesterBased && (
              <button
                onClick={() => setShowSemesterActions(true)}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-700 rounded-xl hover:bg-orange-100 border border-orange-200 transition-all duration-200 font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Complete Semesters
              </button>
            )}

            {academicYear.isSemesterBased && academicYear.semesters && academicYear.semesters.length > 0 && (
              <button
                onClick={() => setShowIndividualSemesterActions(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 border border-indigo-200 transition-all duration-200 font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                Manage Semesters
              </button>
            )}
          </>
        )}

        {/* Rollback Options for Completed/Inactive */}
        {(academicYear.status === 'completed' || academicYear.status === 'inactive') && (
          <div className="flex gap-3">
            <button
              onClick={handleRollbackToActive}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 border border-emerald-200 transition-all duration-200 font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Rollback to Active
            </button>
            <button
              onClick={handleRollbackToInactive}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 border border-blue-200 transition-all duration-200 font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Rollback to Inactive
            </button>
          </div>
        )}

        {/* Archive Button - Visible for all non-archived statuses except active */}
        {!academicYear.isArchived && academicYear.status !== 'active' && (
          <button
            onClick={() => onDelete(getAcademicYearId())}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 border border-amber-200 transition-all duration-200 font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Archive
          </button>
        )}

        {/* View History - Always Available */}
        <button
          onClick={handleViewStatusHistory}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 border border-purple-200 transition-all duration-200 font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          View History
        </button>
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 overflow-hidden ${
      academicYear.isArchived 
        ? 'border-amber-200 bg-amber-25' 
        : isCompletedOrInactive
        ? 'border-slate-200 bg-slate-25'
        : 'border-slate-200 hover:shadow-md hover:border-slate-300'
    }`}>
      {/* Header - Always Visible */}
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full ${
                  academicYear.status === 'active' ? 'bg-emerald-500' :
                  academicYear.status === 'completed' ? 'bg-blue-500' :
                  academicYear.status === 'archived' ? 'bg-amber-500' : 'bg-slate-400'
                }`}></div>
                {/* UPDATED: Show display name with label */}
                <h3 className="text-xl font-semibold text-slate-900 truncate">{getDisplayName()}</h3>
                {/* Optionally, show label as a badge */}
                {/* {academicYear.label && (
                  <span className="ml-2 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200">
                    {academicYear.label}
                  </span>
                )} */}
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(academicYear.status)}`}>
                  {getStatusIcon(academicYear.status)} {academicYear.status?.charAt(0).toUpperCase() + academicYear.status?.slice(1)}
                </span>
                
                {academicYear.isArchived && canRestore && (
                  <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200">
                    🔄 Restorable
                  </span>
                )}
                {academicYear.isArchived && !canRestore && (
                  <span className="px-2.5 py-1 bg-rose-100 text-rose-700 text-xs font-medium rounded-full border border-rose-200">
                    ⏰ Expired
                  </span>
                )}
                {isReadOnly && (
                  <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full border border-slate-200">
                    👁️ Read Only
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{moment(academicYear.startDate).format('MMM DD, YYYY')} - {moment(academicYear.endDate).format('MMM DD, YYYY')}</span>
              </div>
              
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                academicYear.isSemesterBased 
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                  : 'bg-orange-100 text-orange-700 border border-orange-200'
              }`}>
                {academicYear.isSemesterBased ? '🎓 Semester System' : '📚 Annual System'}
                            {/* Show number of terms and term labels for non-semester-based AYs */}
                            {!academicYear.isSemesterBased && academicYear.terms && academicYear.terms.length > 0 && (
                              <span className="ml-4 px-3 py-1.5 rounded-full text-xs font-medium border border-orange-200 bg-orange-50 text-orange-700">
                                🏷️ {academicYear.terms.length} Terms
                                {academicYear.terms.map((t, idx) => (
                                  <span key={idx} className="ml-2 text-orange-900 font-semibold">{t.termLabel}</span>
                                ))}
                              </span>
                            )}
              </span>
            </div>

            {academicYear.isArchived && academicYear.archivedAt && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Archived on {moment(academicYear.archivedAt).format('MMM DD, YYYY')}
                {academicYear.canRestoreUntil && (
                  <span className="ml-2">
                    • Can restore until {moment(academicYear.canRestoreUntil).format('MMM DD, YYYY')}
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 ml-4">
            {/* Edit Button - Always Visible when allowed */}
            {!isReadOnly && !academicYear.isArchived && academicYear.status !== 'completed' && (
              <button
                onClick={() => onEdit(academicYear)}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 border border-slate-200 transition-all duration-200 text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            )}

            {/* Calendar Button - Always Visible */}
            <button
              onClick={() => onOpenCalendar(getAcademicYearId())}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200 transition-all duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Calendar
            </button>

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <svg 
                className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span className="text-sm font-medium">{isExpanded ? 'Less' : 'More'}</span>
            </button>
          </div>
        </div>

        {/* Main Action Buttons - Always Visible */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          {renderMainActionButtons()}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-6 bg-slate-25 border-b border-slate-100">
          {/* System Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Status Overview */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Status Overview
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Current Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(academicYear.status)}`}>
                    {academicYear.status}
                  </span>
                </div>
                {academicYear.status === 'completed' && academicYear.completedAt && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Completed Date</span>
                    <span className="text-sm text-slate-900">{moment(academicYear.completedAt).format('MMM DD, YYYY')}</span>
                  </div>
                )}
                {academicYear.isArchived && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Archive Date</span>
                    <span className="text-sm text-slate-900">{moment(academicYear.archivedAt).format('MMM DD, YYYY')}</span>
                  </div>
                )}
                {/* Terms Overview for non-semester-based AYs */}
                {!academicYear.isSemesterBased && academicYear.terms && academicYear.terms.length > 0 && (
                  <div className="bg-white rounded-xl p-4 border border-orange-200">
                    <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Terms Overview
                    </h4>
                    <div>
                      <div className="mb-2 text-sm text-orange-700 font-medium">Total Terms: {academicYear.terms.length}</div>
                      <ul className="space-y-1">
                        {academicYear.terms.map((t, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="text-orange-900 font-semibold">Term {t.termNumber}:</span>
                            <span className="text-orange-700">{t.termLabel}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Duration */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Duration
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Start Date</span>
                  <span className="text-sm text-slate-900 font-medium">{moment(academicYear.startDate).format('MMM DD, YYYY')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">End Date</span>
                  <span className="text-sm text-slate-900 font-medium">{moment(academicYear.endDate).format('MMM DD, YYYY')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Duration</span>
                  <span className="text-sm text-slate-900 font-medium">
                    {moment(academicYear.endDate).diff(moment(academicYear.startDate), 'months')} months
                  </span>
                </div>
              </div>
            </div>

            {/* Semester Progress (if semester-based) */}
            {academicYear.isSemesterBased && academicYear.semesters && (
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Semester Progress
                </h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-blue-700 font-medium">Odd Semesters</span>
                      <span className="text-slate-600">{completedSemesters.odd}/{semestersByType.odd.length}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${semestersByType.odd.length > 0 ? (completedSemesters.odd / semestersByType.odd.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-purple-700 font-medium">Even Semesters</span>
                      <span className="text-slate-600">{completedSemesters.even}/{semestersByType.even.length}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${semestersByType.even.length > 0 ? (completedSemesters.even / semestersByType.even.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Semester Details - Split into Two Columns for Semester-based Systems */}
          {academicYear.isSemesterBased && academicYear.semesters && academicYear.semesters.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold text-slate-900 mb-4">Semester Details</h4>
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Odd Semesters Column */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
                    <h5 className="font-semibold text-blue-900 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Odd Semesters ({semestersByType.odd.length})
                    </h5>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {semestersByType.odd.map((semester, index) => (
                      <div
                        key={semester._id || `odd-${index}`}
                        className={`p-4 transition-all duration-200 ${
                          semester.status === 'completed'
                            ? 'bg-emerald-25'
                            : semester.status === 'active'
                            ? 'bg-blue-25'
                            : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-slate-900">{semester.semesterName}</h6>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            semester.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : semester.status === 'active'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            {semester.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {moment(semester.startDate).format('MMM DD')} - {moment(semester.endDate).format('MMM DD, YYYY')}
                        </p>
                        {!isReadOnly && academicYear.status === 'active' && (
                          <div className="flex gap-2 mt-2">
                            {semester.status === 'completed' ? (
                              <button
                                onClick={() => handleRollbackSemester(semester._id)}
                                disabled={loading}
                                className="flex-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-xs font-medium"
                              >
                                Rollback
                              </button>
                            ) : (
                              <button
                                onClick={() => handleMarkIndividualSemesterCompleted(semester._id)}
                                disabled={loading}
                                className="flex-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-xs font-medium"
                              >
                                Mark Complete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Even Semesters Column */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-purple-50 px-4 py-3 border-b border-purple-100">
                    <h5 className="font-semibold text-purple-900 flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Even Semesters ({semestersByType.even.length})
                    </h5>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {semestersByType.even.map((semester, index) => (
                      <div
                        key={semester._id || `even-${index}`}
                        className={`p-4 transition-all duration-200 ${
                          semester.status === 'completed'
                            ? 'bg-emerald-25'
                            : semester.status === 'active'
                            ? 'bg-purple-25'
                            : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-slate-900">{semester.semesterName}</h6>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            semester.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : semester.status === 'active'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            {semester.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {moment(semester.startDate).format('MMM DD')} - {moment(semester.endDate).format('MMM DD, YYYY')}
                        </p>
                        {!isReadOnly && academicYear.status === 'active' && (
                          <div className="flex gap-2 mt-2">
                            {semester.status === 'completed' ? (
                              <button
                                onClick={() => handleRollbackSemester(semester._id)}
                                disabled={loading}
                                className="flex-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-xs font-medium"
                              >
                                Rollback
                              </button>
                            ) : (
                              <button
                                onClick={() => handleMarkIndividualSemesterCompleted(semester._id)}
                                disabled={loading}
                                className="flex-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-xs font-medium"
                              >
                                Mark Complete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer - Quick Stats */}
      <div className="px-6 py-4 bg-slate-25 border-t border-slate-100">
        <div className="flex items-center justify-between text-sm text-slate-600">
          <div className="flex items-center gap-4">
            {academicYear.isSemesterBased && academicYear.semesters && (
              <>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  {academicYear.semesters.length} Semesters
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {completedSemesters.total} Completed
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {academicYear.semesters.length - completedSemesters.total} Remaining
                </span>
              </>
            )}
            {academicYear.status === 'completed' && academicYear.completedAt && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Completed {moment(academicYear.completedAt).format('MMM DD, YYYY')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Individual Semester Actions Modal */}
      {showIndividualSemesterActions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-4 border border-slate-200">
            <div className="p-6">
              <div className="flex items-start mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Manage Individual Semesters</h3>
                  <p className="text-slate-600">
                    Mark individual semesters as completed or rollback completed semesters.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 max-h-96 overflow-y-auto">
                {/* Odd Semesters */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-4 text-blue-700">Odd Semesters</h4>
                  <div className="space-y-3">
                    {semestersByType.odd.map((semester) => (
                      <div
                        key={semester._id}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          semester.status === 'completed'
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-slate-900">{semester.semesterName}</h5>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            semester.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {semester.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">
                          {moment(semester.startDate).format('MMM DD')} - {moment(semester.endDate).format('MMM DD, YYYY')}
                        </p>
                        
                        {semester.status === 'completed' ? (
                          <button
                            onClick={() => handleRollbackSemester(semester._id)}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
                          >
                            Rollback Semester
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMarkIndividualSemesterCompleted(semester._id)}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium"
                          >
                            Mark Completed
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Even Semesters */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-4 text-purple-700">Even Semesters</h4>
                  <div className="space-y-3">
                    {semestersByType.even.map((semester) => (
                      <div
                        key={semester._id}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          semester.status === 'completed'
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-purple-50 border-purple-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-slate-900">{semester.semesterName}</h5>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            semester.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {semester.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">
                          {moment(semester.startDate).format('MMM DD')} - {moment(semester.endDate).format('MMM DD, YYYY')}
                        </p>
                        
                        {semester.status === 'completed' ? (
                          <button
                            onClick={() => handleRollbackSemester(semester._id)}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
                          >
                            Rollback Semester
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMarkIndividualSemesterCompleted(semester._id)}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium"
                          >
                            Mark Completed
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex gap-4">
                <button
                  onClick={() => setShowIndividualSemesterActions(false)}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Semester Actions Modal */}
      {showSemesterActions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 border border-slate-200">
            <div className="p-6">
              <div className="flex items-start mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Complete Semesters</h3>
                  <p className="text-slate-600">
                    Mark semester terms as completed. This will close the selected semesters for modifications.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Complete Odd Semesters */}
                <button
                  onClick={() => handleMarkSemesterCompleted('odd')}
                  disabled={loading || completedSemesters.odd === semestersByType.odd.length}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 ${
                    loading || completedSemesters.odd === semestersByType.odd.length
                      ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-blue-600 font-bold">1,3,5</span>
                  </div>
                  <div className="text-left flex-1">
                    <div className="font-semibold">Mark Odd Semesters Complete</div>
                    <div className="text-sm opacity-75">
                      {completedSemesters.odd}/{semestersByType.odd.length} completed
                    </div>
                  </div>
                </button>

                {/* Complete Even Semesters */}
                <button
                  onClick={() => handleMarkSemesterCompleted('even')}
                  disabled={loading || completedSemesters.even === semestersByType.even.length}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 ${
                    loading || completedSemesters.even === semestersByType.even.length
                      ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                  }`}
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <span className="text-purple-600 font-bold">2,4,6</span>
                  </div>
                  <div className="text-left flex-1">
                    <div className="font-semibold">Mark Even Semesters Complete</div>
                    <div className="text-sm opacity-75">
                      {completedSemesters.even}/{semestersByType.even.length} completed
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-8 flex gap-4">
                <button
                  onClick={() => setShowSemesterActions(false)}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicYearCard;